// React & Vendor Libs
const { useCallback, useEffect, useState } = wp.element;

import { apiUrl, restNonce } from "../../settings";
import { postFetch } from "@neko-ui";

export const tableLimit = 10;
export const usefulColumns = [ "ID", "post_date", "post_title", "post_content", "post_status" ];

export const useTableEntryModal = ({ isOpen, tableName, onClose }) => {
  const [table, setTable] = useState();
  const [tableEntries, setTableEntries] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [filteredTableColumns, setFilteredTableColumns] = useState([]);
  const [tableColumnsFilter, setTableColumnsFilter] = useState([]);
  const [tableCount, setTableCount] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!tableName) return;
    updateTable(table, tableName);
  }, [tableName]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const onCloseTableModal = useCallback(() => {
    setOpen(false);
    onClose();
  }, [onClose]);

  const getTable = useCallback(async (table, page = 1) => {
    try {
      const offset = tableLimit * (page - 1);
      const response = await postFetch(`${apiUrl}/table`, {
        json: { table, offset },
        nonce: restNonce,
      });
      if (response.success) {
        return { entries: response.data, count: response.count };
      } else {
        if (response.message) alert(response.message);
      }
    } catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    return null;
  }, []);

  const getTableColumnsAndFilters = useCallback((entries) => {
    const tableColumns = entries.length
      ? Object.keys(entries.at(0)).map((o) => {
          const title = o.replace(/_/g, " ");
          const cleanTitle = title.replace(/^post/, "").replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));
          return { accessor: o, title: cleanTitle, sortable: false, style: { width: "auto", maxWidth: "400px", minWidth: "50px" }};
        })
      : [];
    var tableColumnsFilter = tableColumns.map((v) => {
      return { label: v.title, accessor: v.accessor, checked: true };
    });

    // Apply useful columns.
    if (tableColumns.length > 8) {
      tableColumnsFilter = tableColumnsFilter.map((v) => {
        if (usefulColumns.includes(v.accessor)) {
          return { ...v, checked: true };
        }
        return { ...v, checked: false };
      });
    }
    // Show 8 columns at least.
    if (!tableColumnsFilter.filter(v => v.checked).length) {
      tableColumnsFilter = tableColumnsFilter.map((v, i) => {
        if (i < 8) {
          return { ...v, checked: true };
        }
        return { ...v, checked: false };
      });
    }
    return { tableColumns, tableColumnsFilter };
  }, []);

  const updateTableColumnsFilter = useCallback(
    (tableColumns, newTableColumnsFilter) => {
      const showColumnAccessors = newTableColumnsFilter.filter((v) => v.checked).map((v) => v.accessor);
      const newFilteredTableColumns = tableColumns.filter((v) => showColumnAccessors.includes(v.accessor));
      setTableColumnsFilter(newTableColumnsFilter);
      setFilteredTableColumns(newFilteredTableColumns);
    }, []);

  const updateTable = useCallback(async (prevTable, nextTable) => {
    if (prevTable === nextTable) {
      return;
    }
    const { entries, count } = await getTable(nextTable);
    const { tableColumns, tableColumnsFilter } = getTableColumnsAndFilters(entries);
    setTablePage(1);
    setTable(nextTable);
    setTableCount(count);
    setTableEntries(entries);
    setTableColumns(tableColumns);
    updateTableColumnsFilter(tableColumns, tableColumnsFilter);
  }, [getTable, getTableColumnsAndFilters]);

  const updateTablePage = useCallback(async (newPage) => {
    const { entries } = await getTable(table, newPage);
    setTablePage(newPage);
    setTableEntries(entries);
  }, [table, getTable]);

  return {
    table,
    tableEntries,
    tableColumns,
    filteredTableColumns,
    tableColumnsFilter,
    tableCount,
    tablePage,
    open,
    onCloseTableModal,
    updateTablePage,
    updateTableColumnsFilter,
  };
};
